// Small formatting helpers shared by the operator console, supervisor events and setup.

import type { Actor, EventAlarm, EventDetail, Severity } from '../api.ts';

export type SevCls = 'crit' | 'high' | 'med' | 'low' | 'info';

export const SEV_CLS: Record<Severity, SevCls> = { critical: 'crit', high: 'high', medium: 'med', low: 'low', info: 'info' };
export const SEV_LABEL: Record<Severity, string> = { critical: 'CRITICAL', high: 'HIGH', medium: 'MEDIUM', low: 'LOW', info: 'INFO' };
export const SEV_RANK: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

const p2 = (n: number) => String(n).padStart(2, '0');

/** "mm:ss" or "h:mm:ss" (prototype dur()). */
export function dur(ms: number): string {
  let s = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  s %= 60;
  return h ? `${h}:${p2(m)}:${p2(s)}` : `${p2(m)}:${p2(s)}`;
}

/** "T+00:41" offset from the first alarm (prototype toff()). */
export function toff(ms: number): string {
  return `T${ms < 0 ? '−' : '+'}${dur(Math.abs(ms))}`;
}

export function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** When the alarm happened: the vendor time, or when we received it. */
export function alarmMs(a: EventAlarm): number {
  return ms(a.occurredAt) ?? ms(a.receivedAt) ?? 0;
}

/** "Cam 04" from "Cam 04 · Fence East". */
export function camShort(name: string | null | undefined): string {
  if (!name) return 'Unmapped camera';
  return name.split(' · ')[0] ?? name;
}

export function actorName(a: Actor | null | undefined): string {
  if (!a) return 'Unknown';
  if (a.name) return a.name;
  return a.type === 'system' ? 'System' : a.type === 'agent' ? 'AI agent' : a.type;
}

/** CSS class for who did something: AI is violet, people accent, system muted. */
export function actorCls(a: Actor | null | undefined): string {
  if (!a) return 'a-system';
  return `a-${a.type}`;
}

export function addressLine(e: EventDetail): string {
  const a = e.site.address;
  if (!a) return '';
  const street = [a.line1, a.line2].filter(Boolean).join(', ');
  const place = [a.city, [a.region, a.postal_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, place].filter(Boolean).join(', ');
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Ignore hotkeys while the user types. */
export function isTyping(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (t as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'range'].includes(type);
  }
  return false;
}

/** "line_crossing" → "Line crossing" (analytics and categories arrive as snake_case codes). */
export function human(s: string | null | undefined, fallback = '—'): string {
  if (!s) return fallback;
  const t = s.replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** "+14805550134" → "(480) 555-0134"; other numbers are shown as stored. */
export function fmtPhone(e164: string | null | undefined, ext?: string | null): string {
  if (!e164) return '—';
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  const base = m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
  return ext ? `${base} ext. ${ext}` : base;
}

/**
 * Stable alarm order. The API returns alarms "oldest first", but alarms with the same occurredAt
 * can come back in a different order between responses, which would renumber the A1…An chips.
 */
export function normalizeEvent(d: EventDetail): EventDetail {
  const alarms = d.alarms.slice().sort((a, b) => alarmMs(a) - alarmMs(b) || (ms(a.receivedAt) ?? 0) - (ms(b.receivedAt) ?? 0) || a.id.localeCompare(b.id));
  return { ...d, alarms };
}

export function str(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null;
}
